import { useModal } from '@ebay/nice-modal-react';
import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Divider, MainTitle, StickyBottomWrapper, Table, TableCell } from '@repo/ui';
import { format } from 'date-fns';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';

import { AutographDrawer } from './AutographDrawer';

import { bznavRefundGetDeligationInfoOfUserQuery } from '@/__generated__/bznavRefundGetDeligationInfoOfUserQuery.graphql';
import { useLookupResultRefundData } from '@/components/tax-refund/lookup/result/LookupResultContext';
import { getDeligationInfoOfUser } from '@/graphql/query/tax-refund/bznav-refund';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { poaFormValuesAtom } from '@/lib/stores/power-of-attorney.ts';
import { BusinessType } from '@/lib/types/request';

const POWER_OF_ATTORNEY_PREVIEW_PATH = '/tax/refund/power-of-attorney/preview';

export const PoaSign = ({ businessType }: { businessType: BusinessType }) => {
  const router = useRouter();
  const [formValues, setFormValues] = useAtom(poaFormValuesAtom);
  const autographDrawer = useModal(AutographDrawer);
  const { refundId } = useLookupResultRefundData();
  const data = useLazyLoadQuery<bznavRefundGetDeligationInfoOfUserQuery>(getDeligationInfoOfUser, { refundId }, { fetchPolicy: 'network-only' });
  const { sendClickEvent } = useUserEventLogger();

  const showPreviewPage = () => {
    sendClickEvent('request_before_autograph_poa-preview');
    router.push({
      pathname: POWER_OF_ATTORNEY_PREVIEW_PATH,
      query: router.query
    });
  };

  const openAutographDrawer = async () => {
    sendClickEvent('request_before_autograph_confirm-and-sign');
    const newAutographDataUrl = await autographDrawer.show({
      name: formValues.principalName,
      keepMounted: true
    });
    const autographDataUrl = typeof newAutographDataUrl === 'string' ? newAutographDataUrl : '';

    setFormValues((prev) => ({
      ...prev,
      signedAt: new Date().toISOString(),
      autographDataUrl
    }));

    if (autographDataUrl) {
      router.push({
        pathname: COMMON_PATHS.위임장제출,
        query: { type: businessType }
      });
    }
  };

  useEffect(() => {
    const principalName = data.getDeligationInfoOfUser?.name ?? '';
    const principalRrn = data.getDeligationInfoOfUser?.birthDate ? `${data.getDeligationInfoOfUser?.birthDate}-*******` : '';
    const principalAddress = data.getDeligationInfoOfUser?.address ?? '';

    setFormValues({
      principalName,
      principalRrn,
      principalAddress,
      issuedDate: format(new Date(), 'yyyy년 M월 d일'),
      signedAt: '',
      autographDataUrl: '',
      isReady: true
    });
  }, [data.getDeligationInfoOfUser?.birthDate, data.getDeligationInfoOfUser?.address, data.getDeligationInfoOfUser?.name, setFormValues]);

  return (
    <>
      <section className={'flex flex-col flex-1 items-center pb-spacious-small'}>
        <div className="container-medium container-padding-medium pb-normal-medium w-full">
          <MainTitle title={'환급 진행을 위해\n업무 위임에 동의해주세요'} />
        </div>
        <div className="flex flex-col container-medium container-padding-medium w-full pb-normal-large">
          <MainTitle title={'수임자 정보'} size={'xsmall'} className={'!pb-normal-medium'} />
          <PartyInformationRow label={'수임자'} value={'지수회계법인'} />
          <PartyInformationRow label={'사업자등록번호'} value={'711-88-02969'} />
          <PartyInformationRow label={'주소'} value={'서울특별시 송파구 법원로 128, 씨1613호(문정동, 문정역에스케이브이1)'} />
          <PartyInformationRow label={'전화번호'} value={'02-6080-1954'} />
          <PartyInformationRow label={'팩스번호'} value={'02-6969-9436'} />
          <PartyInformationRow label={'자격'} value={'공인회계사'} />
          <PartyInformationRow label={'관계'} value={'경정청구 세무대리인'} />
        </div>
        <Divider size={'large'} className={'container-medium'} />
        <div className={'container-medium container-padding-medium w-full pt-normal-small pb-normal-large'}>
          <MainTitle title={'위임 권한의 범위'} size={'xsmall'} />
          <ul className="flex flex-col gap-normal-small list-disc marker:text-[10px] pl-5 mt-normal-small body-medium-regular text-neutral-foreground-main">
            <li>경정청구 대상 세액 검토: 과오납 내역 및 세액공제감면 누락 사항 등 환급 가능성 검토</li>
            <li>경정청구 대리: 경정청구서의 작성, 제출(접수) 및 과세관청에 대한 의견 진술, 자료 제출 등 일체의 절차</li>
            <li>
              결과 확인 및 통지 수령: 경정청구 결과에 대한 확인(인용 여부, 결정 금액, 환급 결정일, 환급 계좌, 기각 시 사유 등) 및 관련 통지(민원 처리 결과,
              환급금 결정 통지 등)의 수령 권한
            </li>
            <li>기타: 위 각 호에 부수되는 세무 상담 및 결과 통보 업무</li>
          </ul>
        </div>
        <Divider size={'large'} className={'container-medium'} />
        <div className="container-medium container-padding-medium w-full pt-normal-small pb-normal-large">
          <MainTitle title={'위임자 정보'} size={'xsmall'} className={'!pb-normal-medium'} />
          <PartyInformationRow label={'위임자 성명'} value={formValues.principalName} />
          <PartyInformationRow label={'주민등록번호'} value={formValues.principalRrn} />
          <PartyInformationRow label={'주소'} value={formValues.principalAddress} />
        </div>
        <div className="container-medium container-padding-medium w-full">
          <BoxButton size={'medium'} className={'w-full'} variant={'tonal-secondary'} onClick={showPreviewPage}>
            위임장 미리보기
          </BoxButton>
        </div>
      </section>

      <StickyBottomWrapper className={'sticky'}>
        <BoxButton size={'large'} className={'w-full'} onClick={openAutographDrawer}>
          확인 후 자필서명
        </BoxButton>
      </StickyBottomWrapper>
    </>
  );
};

const PartyInformationRow = ({ label, value }: { label: string; value: string }) => {
  return (
    <Table
      className={'flex justify-between'}
      headerCell={<TableCell variant={'header'}>{label}</TableCell>}
      contentCell={
        <TableCell variant={'content'} className={'text-end'}>
          {value}
        </TableCell>
      }
    />
  );
};
